/**
 * OAuth Callback Success Page
 *
 * Shown after successful OAuth login. Fetches user data and redirects to dashboard.
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/features/auth/store';
import { api } from '@/lib/api';

export const Route = createFileRoute('/auth/callback/success')({
  component: OAuthSuccessPage,
});

function OAuthSuccessPage(): React.JSX.Element {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Fetch user data from the session cookie
    const fetchUser = async () => {
      try {
        const data = await api.get<{ user: { id: string; name: string; email: string } }>('/auth/me');

        if (data.user) {
          // Update auth store with user data
          // Note: We use a placeholder token since we're using HTTP-only cookies
          setAuth(data.user, 'session-cookie');

          // Redirect to dashboard
          setTimeout(() => {
            navigate({ to: '/dashboard' });
          }, 500);
        } else {
          throw new Error('No user data returned');
        }
      } catch (err) {
        console.error('OAuth success handler error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    fetchUser();
  }, [navigate, setAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle>Authentication Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <a href="/login" className="text-primary hover:underline">
              Return to login
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle>Login Successful</CardTitle>
          <CardDescription>
            You have been successfully authenticated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Redirecting to dashboard...</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
